import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutGrid,
  Briefcase,
  LineChart,
  Bot,
  Inbox,
  Settings,
  LogOut,
  Hammer,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import Logo from "@/components/Logo";

const NAV = [
  { to: "/dashboard", label: "Overview", Icon: LayoutGrid, end: true },
  { to: "/dashboard/sales", label: "Jobs", Icon: Briefcase },
  { to: "/dashboard/market", label: "Storm Watch", Icon: LineChart },
  { to: "/dashboard/chat", label: "AI Sentinel", Icon: Bot },
];

const ADMIN_NAV = [
  { to: "/dashboard/messages", label: "Inbound", Icon: Inbox },
  { to: "/dashboard/leads", label: "Leads", Icon: Briefcase },
  { to: "/dashboard/applications", label: "Network Apps", Icon: Hammer },
];

export default function Sidebar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <aside className="hidden lg:flex flex-col fixed left-0 top-0 bottom-0 w-64 border-r border-white/5 bg-[#0a0a0b] z-30" data-testid="dashboard-sidebar">
      <div className="h-16 flex items-center px-6 border-b border-white/5">
        <Logo />
      </div>

      <div className="px-3 py-6 flex-1 overflow-y-auto scroll-thin">
        <div className="font-mono-apex text-[9px] uppercase tracking-[0.3em] text-zinc-600 px-3 mb-3">
          intelligence
        </div>
        <nav className="space-y-1">
          {NAV.map(({ to, label, Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 group transition-colors ${
                  isActive
                    ? "bg-white/[0.03] text-white border-l-2 border-[#dc2626]"
                    : "text-zinc-400 hover:text-white hover:bg-white/[0.02] border-l-2 border-transparent"
                }`
              }
              data-testid={`sidebar-${label.toLowerCase().replace(/\s+/g, "-")}-link`}
            >
              <Icon size={16} strokeWidth={1.5} />
              <span className="font-mono-apex text-[11px] uppercase tracking-[0.18em]">{label}</span>
            </NavLink>
          ))}
        </nav>

        {user?.role === "admin" && (
          <>
            <div className="font-mono-apex text-[9px] uppercase tracking-[0.3em] text-zinc-600 px-3 mt-8 mb-3">
              admin
            </div>
            <nav className="space-y-1">
              {ADMIN_NAV.map(({ to, label, Icon }) => (
                <NavLink
                  key={to}
                  to={to}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2.5 transition-colors ${
                      isActive
                        ? "bg-white/[0.03] text-white border-l-2 border-[#dc2626]"
                        : "text-zinc-400 hover:text-white hover:bg-white/[0.02] border-l-2 border-transparent"
                    }`
                  }
                  data-testid={`sidebar-${label.toLowerCase()}-link`}
                >
                  <Icon size={16} strokeWidth={1.5} />
                  <span className="font-mono-apex text-[11px] uppercase tracking-[0.18em]">{label}</span>
                </NavLink>
              ))}
            </nav>
          </>
        )}

        <div className="font-mono-apex text-[9px] uppercase tracking-[0.3em] text-zinc-600 px-3 mt-8 mb-3">
          system
        </div>
        <nav className="space-y-1">
          <NavLink
            to="/dashboard/settings"
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 transition-colors ${
                isActive
                  ? "bg-white/[0.03] text-white border-l-2 border-[#dc2626]"
                  : "text-zinc-400 hover:text-white hover:bg-white/[0.02] border-l-2 border-transparent"
              }`
            }
            data-testid="sidebar-settings-link"
          >
            <Settings size={16} strokeWidth={1.5} />
            <span className="font-mono-apex text-[11px] uppercase tracking-[0.18em]">Settings</span>
          </NavLink>
        </nav>
      </div>

      <div className="border-t border-white/5 p-4">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-9 h-9 bg-[#1a1a1e] border border-white/10 flex items-center justify-center">
            {user?.picture ? (
              <img src={user.picture} alt="" className="w-full h-full object-cover" />
            ) : (
              <span className="font-mono-apex text-xs text-zinc-300">
                {(user?.name || "U").slice(0, 2).toUpperCase()}
              </span>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-body text-sm text-white truncate" data-testid="sidebar-user-name">
              {user?.name || "Operator"}
            </div>
            <div className="font-mono-apex text-[9px] uppercase tracking-[0.2em] text-zinc-500 truncate">
              {user?.role === "admin" ? "admin · " : ""}
              {user?.email}
            </div>
          </div>
        </div>
        <button
          onClick={async () => {
            await logout();
            navigate("/");
          }}
          className="w-full flex items-center justify-center gap-2 btn-secondary-apex py-2.5 font-mono-apex text-[10px] uppercase tracking-[0.25em]"
          data-testid="sidebar-logout-btn"
        >
          <LogOut size={14} strokeWidth={1.5} /> Sign out
        </button>
      </div>
    </aside>
  );
}
